import { useCallback, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useLatest from './useLatest';

const getSearchParam = (search, param) => {
  const searchParams = new URLSearchParams(search);
  return searchParams.get(param);
};

const setSearchParam = (search, param, value) => {
  const searchParams = new URLSearchParams(search);

  Boolean(value) ? searchParams.set(param, value) : searchParams.delete(param);

  return searchParams.toString();
};

const defaultDeserialize = (v) => v;
const defaultSerialize = String;

const useSearchParams = ({
  name,
  serialize = defaultSerialize,
  deserialize = defaultDeserialize,
}) => {
  const history = useHistory();
  const { search } = useLocation();

  const memoizedSerialize = useCallback((v) => serialize(v), []);
  const memoizedDeserialize = useCallback((v) => deserialize(v), []);

  const [value, setValue] = useState(() => {
    const initialValue = memoizedDeserialize(getSearchParam(search, name));

    return initialValue;
  });

  const latestValue = useLatest(value);

  const updateValue = useCallback(
    (newValue) => {
      const actualNewValue =
        typeof newValue === 'function'
          ? newValue(latestValue.current)
          : newValue;

      const newSearch = setSearchParam(
        search,
        name,
        memoizedSerialize(actualNewValue)
      );

      history.replace({
        search: newSearch,
      });
    },
    [latestValue, history.replace, search, name, memoizedSerialize]
  );

  useEffect(() => {
    const value = memoizedDeserialize(getSearchParam(search, name));

    setValue(value);
  }, [search, name, memoizedDeserialize]);

  return [value, updateValue];
};

export default useSearchParams;
